import { useEffect, useRef, useState } from "react";
import { formatMultiplier } from "../lib/format.js";

interface MultiplierProps {
  value: number;
  state: "live" | "win" | "bust" | "idle";
  crackPulseKey: number;
  size?: "hero" | "compact";
}

export function Multiplier({ value, state, crackPulseKey, size = "hero" }: MultiplierProps) {
  const [shake, setShake] = useState(false);
  const prevKey = useRef(crackPulseKey);

  useEffect(() => {
    if (crackPulseKey !== prevKey.current) {
      prevKey.current = crackPulseKey;
      setShake(true);
      const t = setTimeout(() => setShake(false), 180);
      return () => clearTimeout(t);
    }
  }, [crackPulseKey]);

  const colorCls =
    state === "win" ? "display-gold" : state === "bust" ? "display-red" : "display-cyan";

  const haloBg =
    state === "win"
      ? "radial-gradient(circle at center, rgba(212,162,76,0.18), transparent 60%)"
      : state === "bust"
      ? "radial-gradient(circle at center, rgba(255,59,71,0.18), transparent 60%)"
      : "radial-gradient(circle at center, rgba(0,229,255,0.14), transparent 60%)";

  const label =
    state === "win" ? "CASHED OUT" : state === "bust" ? "VAULT SEALED" : "LIVE MULTIPLIER";

  const pulseCls =
    shake && state === "live"
      ? "vb-crack-shake vb-chromatic-cyan"
      : state === "bust"
      ? "vb-chromatic-red"
      : state === "win"
      ? "vb-win-scale"
      : "";

  return (
    <div
      className="vb-multiplier-frame"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "8px 0",
      }}
    >
      {size === "hero" && (
        <div
          aria-hidden
          style={{ position: "absolute", inset: 0, background: haloBg, pointerEvents: "none" }}
        />
      )}
      {state === "live" && size === "hero" && <div className="vb-scanline" style={{ top: 0 }} />}
      {(state === "live" || state === "idle") && (
        <span className="label" style={{ position: "relative" }}>
          {label}
        </span>
      )}
      <span
        className={`display-xl ${colorCls} ${pulseCls}`}
        style={{
          position: "relative",
          fontSize: size === "compact" ? "clamp(40px, 6vw, 60px)" : "clamp(56px, 9vw, 88px)",
          lineHeight: 1,
        }}
        aria-live="off"
      >
        {formatMultiplier(value)}
      </span>
    </div>
  );
}
